import React, { useState, useEffect, useRef } from 'react';
import { useMyContext } from '../../../../context/MyContext';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import axios from 'axios';
import { toast } from 'react-toastify';
import '../../../../css/custom-scrollbar.css';
import { Addwarehouse, Base_Url, getpincode, fetchUser } from '../../../../config/config';
import Select from "react-dropdown-select";
import {
  Building2,
  User,
  Phone,
  Mail,
  MapPin,
  Globe,
  Hash,
  Loader2,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  X,
  ChevronDown,
  RotateCcw,
  Sparkles
} from "lucide-react";

// Form validation schema with friendly, forgiving rules
const schema = z.object({
  facilityName: z
    .string()
    .min(1, "Facility name is required")
    .regex(/^[A-Za-z0-9\s\-_.,()&]+$/, "Only letters, numbers and basic symbols (-_.,()&) allowed"),
  contactPerson: z
    .string()
    .min(1, "Contact person name is required")
    .regex(/^[A-Za-z\s.]+$/, "Only letters and spaces allowed"),
  phone: z
    .string()
    .length(10, "Phone number must be exactly 10 digits")
    .regex(/^\d{10}$/, "Phone number must contain only numbers"),
  pincode: z
    .string()
    .length(6, "Pincode must be exactly 6 digits")
    .regex(/^\d{6}$/, "Pincode must contain only numbers"),
  city: z.string().min(1, "City is required (auto-fetched from pincode)"),
  state: z.string().min(1, "State is required (auto-fetched from pincode)"),
  email: z
    .string()
    .optional()
    .refine((val) => !val || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val), "Invalid email address"),
  address: z
    .string()
    .min(1, "Complete address is required")
    .regex(/^[^#\\%&;]*$/, "Address cannot contain special characters (# \\ % & ;)"),
  returnAddress: z.string().optional(),
  returnState: z.string().optional(),
  returnCity: z.string().optional(),
  returnCountry: z.string().optional(),
  returnPincode: z.string().optional()
});

const AddWarehouse = ({ onClose, onSuccess }) => {
  const { isDarkMode } = useMyContext();
  const [showReturnAddress, setShowReturnAddress] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [retailerId, setRetailerId] = useState('');
  const [retailerIdData, setRetailerIdData] = useState([]);
  const [retailerError, setRetailerError] = useState('');
  const [isFetchingPincode, setIsFetchingPincode] = useState(false);
  const [pincodeVerified, setPincodeVerified] = useState(false);
  const [isFetchingReturnPincode, setIsFetchingReturnPincode] = useState(false);
  const [returnPincodeVerified, setReturnPincodeVerified] = useState(false);
  const [apiError, setApiError] = useState(null);

  const scrollRef = useRef(null);
  const role = localStorage.getItem("role");

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    setError,
    clearErrors
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      facilityName: '',
      contactPerson: '',
      phone: '',
      pincode: '',
      city: '',
      state: '',
      email: '',
      address: '',
      returnAddress: '',
      returnState: '',
      returnCity: '',
      returnCountry: 'India',
      returnPincode: ''
    }
  });

  useEffect(() => {
    if (role === 'admin') {
      fetchRetailers();
    }
  }, [role]);

  // Fetch retailers list for admin
  const fetchRetailers = async () => {
    const token = localStorage.getItem('token');
    const cusid = localStorage.getItem('cus_id');
    try {
      const response = await fetch(Base_Url + fetchUser, {
        method: 'GET',
        headers: {
          'token': token,
          'cus_id': cusid,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      if (data && data.data) {
        const transformedData = data.data.map(retailer => ({
          value: retailer.id,
          label: `${retailer.name} (ID: ${retailer.id})`,
        }));
        setRetailerIdData(transformedData);
      }
    } catch (error) {
      console.error('Error fetching retailers:', error);
    }
  };

  // Pincode auto-lookup for facility
  const fetchPincodeData = async (pin) => {
    if (!pin || pin.length !== 6) return;
    setIsFetchingPincode(true);
    setPincodeVerified(false);
    const token = localStorage.getItem('token');
    const cus_id = localStorage.getItem('cus_id');
    const urlgetPincode = `${Base_Url}${getpincode}?pincode=${pin}`;

    try {
      const response = await fetch(urlgetPincode, {
        method: 'GET',
        headers: {
          'token': token,
          'cus_id': cus_id,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data && data.status === "SUCCESS" && data.data && data.data.length > 0) {
        let { city, state } = data.data[0];
        city = city ? city.charAt(0).toUpperCase() + city.slice(1).toLowerCase() : '';
        state = state ? state.charAt(0).toUpperCase() + state.slice(1).toLowerCase() : '';
        setValue("city", city, { shouldValidate: true });
        setValue("state", state, { shouldValidate: true });
        setPincodeVerified(true);
        clearErrors(["city", "state", "pincode"]);
      } else {
        setValue("city", "");
        setValue("state", "");
        setPincodeVerified(false);
        setError("pincode", {
          type: "manual",
          message: data?.message || "Invalid pincode or service not available"
        });
      }
    } catch (error) {
      setPincodeVerified(false);
      setError("pincode", { type: "manual", message: "Failed to verify pincode" });
    } finally {
      setIsFetchingPincode(false);
    }
  };

  // Pincode auto-lookup for return address
  const fetchReturnPincodeData = async (pin) => {
    if (!pin || pin.length !== 6) return;
    setIsFetchingReturnPincode(true);
    setReturnPincodeVerified(false);
    const token = localStorage.getItem('token');
    const cus_id = localStorage.getItem('cus_id');
    const urlgetPincode = `${Base_Url}${getpincode}?pincode=${pin}`;

    try {
      const response = await fetch(urlgetPincode, {
        method: 'GET',
        headers: {
          'token': token,
          'cus_id': cus_id,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data && data.status === "SUCCESS" && data.data && data.data.length > 0) {
        let { city, state } = data.data[0];
        city = city ? city.charAt(0).toUpperCase() + city.slice(1).toLowerCase() : '';
        state = state ? state.charAt(0).toUpperCase() + state.slice(1).toLowerCase() : '';
        setValue("returnCity", city, { shouldValidate: true });
        setValue("returnState", state, { shouldValidate: true });
        setReturnPincodeVerified(true);
        clearErrors(["returnCity", "returnState", "returnPincode"]);
      } else {
        setValue("returnCity", "");
        setValue("returnState", "");
        setReturnPincodeVerified(false);
        setError("returnPincode", {
          type: "manual",
          message: data?.message || "Invalid return pincode"
        });
      }
    } catch (error) {
      setReturnPincodeVerified(false);
      setError("returnPincode", { type: "manual", message: "Failed to verify return pincode" });
    } finally {
      setIsFetchingReturnPincode(false);
    }
  };

  const handlePincodeChange = (e) => {
    const rawVal = e.target.value.replace(/\D/g, '').slice(0, 6);
    setValue("pincode", rawVal, { shouldValidate: true });
    if (rawVal.length === 6) {
      fetchPincodeData(rawVal);
    } else {
      setPincodeVerified(false);
      setValue("city", "");
      setValue("state", "");
    }
  };

  const handleReturnPincodeChange = (e) => {
    const rawVal = e.target.value.replace(/\D/g, '').slice(0, 6);
    setValue("returnPincode", rawVal, { shouldValidate: true });
    if (rawVal.length === 6) {
      fetchReturnPincodeData(rawVal);
    } else {
      setReturnPincodeVerified(false);
      setValue("returnCity", "");
      setValue("returnState", "");
    }
  };

  const handlePhoneChange = (e) => {
    const rawVal = e.target.value.replace(/\D/g, '').slice(0, 10);
    setValue("phone", rawVal, { shouldValidate: true });
  };

  const handleRetailerSelect = (values) => {
    const selected = values && values.length > 0 ? values[0] : null;
    if (selected) {
      setRetailerId(selected.value);
      setRetailerError('');
    } else {
      setRetailerId('');
    }
  };

  // Client validation failure
  const onErrors = (validationErrors) => {
    setApiError({
      message: "Please fill out all required fields correctly before submitting.",
      details: Object.values(validationErrors).map(err => err?.message).filter(Boolean)
    });
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Submit Handler
  const onSubmit = async (data) => {
    // Admin must select a retailer
    if (role === 'admin' && !retailerId) {
      setRetailerError("Please select a retailer");
      setApiError({ message: "Please select a retailer to assign this warehouse." });
      if (scrollRef.current) scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setApiError(null);
    setIsSubmitting(true);

    const token = localStorage.getItem('token');
    const adminId = localStorage.getItem('cus_id');
    const effectiveCustomerId = role === 'admin' ? retailerId : adminId;

    const payload = {
      name: data.facilityName.trim(),
      email: data.email ? data.email.trim() : '',
      phone: data.phone.trim(),
      address: data.address.trim(),
      pincode: data.pincode.trim(),
      city: data.city.trim(),
      contact_person: data.contactPerson.trim(),
      state: data.state.trim(),
      country: "India",
      customer_id: effectiveCustomerId,
      return_warehouse: showReturnAddress ? "true" : "false",
      return_address: showReturnAddress ? (data.returnAddress || '').trim() : '',
      return_city: showReturnAddress ? (data.returnCity || '').trim() : '',
      return_state: showReturnAddress ? (data.returnState || '').trim() : '',
      return_country: 'India',
      return_pincode: showReturnAddress ? (data.returnPincode || '').trim() : '',
    };

    try {
      const response = await axios.post(Base_Url + Addwarehouse, payload, {
        headers: {
          'token': token,
          'cus_id': adminId,
          'Content-Type': 'application/json',
        },
      });

      if (response.data.status === "SUCCESS") {
        toast.success(response.data.message || "Warehouse created successfully!");
        reset();
        if (onSuccess) {
          onSuccess();
        } else if (onClose) {
          onClose();
        }
      } else {
        // Backend returned failure status
        const errMsg = response.data.message || "Failed to create warehouse";
        setApiError({
          message: errMsg,
          details: response.data.errors
        });

        // Map backend errors to form fields
        if (response.data.errors && typeof response.data.errors === 'object') {
          mapBackendErrorsToFields(response.data.errors);
        }
        toast.error(errMsg);
        if (scrollRef.current) scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error("Error creating warehouse:", error);
      const resData = error.response?.data;
      const errMsg = resData?.message || error.message || "An unexpected error occurred while saving.";
      const backendErrors = resData?.errors;

      setApiError({
        message: errMsg,
        details: backendErrors
      });

      if (backendErrors && typeof backendErrors === 'object') {
        mapBackendErrorsToFields(backendErrors);
      }

      toast.error(errMsg);
      if (scrollRef.current) scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper to map backend error keys to React Hook Form field names
  const mapBackendErrorsToFields = (backendErrors) => {
    const fieldMapping = {
      name: 'facilityName',
      facility_name: 'facilityName',
      contact_person: 'contactPerson',
      phone: 'phone',
      mobile: 'phone',
      email: 'email',
      pincode: 'pincode',
      city: 'city',
      state: 'state',
      address: 'address',
      return_address: 'returnAddress',
      return_pincode: 'returnPincode',
      return_city: 'returnCity',
      return_state: 'returnState',
      customer_id: 'retailer'
    };

    Object.entries(backendErrors).forEach(([key, val]) => {
      const formField = fieldMapping[key];
      const message = Array.isArray(val) ? val[0] : String(val);
      if (formField && formField !== 'retailer') {
        setError(formField, { type: 'server', message });
      } else if (formField === 'retailer') {
        setRetailerError(message);
      }
    });
  };

  const inputContainerClass = (hasError) =>
    `relative flex items-center rounded-xl border transition-all duration-200 ${
      hasError
        ? 'border-rose-400 dark:border-rose-500 bg-rose-50/40 dark:bg-rose-950/20 ring-2 ring-rose-500/20'
        : isDarkMode
          ? 'border-slate-700 bg-slate-900/70 focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500/20'
          : 'border-slate-200 bg-slate-50/60 focus-within:bg-white focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500/20 shadow-2xs'
    }`;

  const inputClass =
    `w-full py-2.5 pl-10 pr-3 text-xs bg-transparent rounded-xl outline-none transition-colors ${
      isDarkMode
        ? 'text-white placeholder-slate-500 disabled:text-slate-400'
        : 'text-slate-900 placeholder-slate-400 disabled:text-slate-500'
    } disabled:cursor-not-allowed`;

  return (
    <div className="flex flex-col h-full flex-1 min-h-0 overflow-hidden">
      {/* Scrollable Form Body */}
      <form
        onSubmit={handleSubmit(onSubmit, onErrors)}
        className="flex flex-col h-full flex-1 min-h-0 overflow-hidden"
      >
        <div
          ref={scrollRef}
          className="flex-1 min-h-0 overflow-y-auto custom-scrollbar p-5 space-y-4"
        >
          {/* Top API / Validation Error Banner */}
          {apiError && (
            <div className="rounded-2xl border border-rose-300 dark:border-rose-900/60 bg-gradient-to-r from-rose-50 via-rose-50/80 to-amber-50/60 dark:from-rose-950/50 dark:via-rose-950/40 dark:to-slate-900 p-4 text-xs text-rose-800 dark:text-rose-200 shadow-sm animate-fadeIn flex items-start gap-3">
              <div className="w-7 h-7 rounded-xl bg-rose-100 dark:bg-rose-900/50 text-rose-600 dark:text-rose-400 flex items-center justify-center shrink-0">
                <AlertTriangle size={15} />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-extrabold text-[12.5px] text-rose-900 dark:text-rose-200">
                  {apiError.message || "Validation Error"}
                </h4>
                {apiError.details && typeof apiError.details === 'object' && !Array.isArray(apiError.details) && (
                  <ul className="mt-1.5 space-y-1 text-[11px] text-rose-700 dark:text-rose-300">
                    {Object.entries(apiError.details).map(([key, msg]) => (
                      <li key={key} className="flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0" />
                        <span className="font-bold capitalize">{key.replace('_', ' ')}:</span>
                        <span>{Array.isArray(msg) ? msg.join(', ') : String(msg)}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {Array.isArray(apiError.details) && apiError.details.length > 0 && (
                  <ul className="mt-1.5 space-y-1 text-[11px] text-rose-700 dark:text-rose-300">
                    {apiError.details.map((msg, idx) => (
                      <li key={idx} className="flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0" />
                        <span>{String(msg)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <button
                type="button"
                onClick={() => setApiError(null)}
                className="text-rose-400 hover:text-rose-700 dark:hover:text-rose-200 p-1 rounded-lg"
                title="Dismiss"
              >
                <X size={15} />
              </button>
            </div>
          )}

          {/* ================= SECTION 1: FACILITY DETAILS ================= */}
          <div className={`rounded-2xl border p-4 sm:p-4.5 transition-colors ${
            isDarkMode
              ? 'bg-slate-900/60 border-slate-800'
              : 'bg-white border-slate-200/90 shadow-2xs'
          }`}>
            <div className="flex items-center justify-between pb-3 mb-3.5 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
                  <Building2 size={15} />
                </div>
                <div>
                  <h3 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">
                    Facility Details
                  </h3>
                  <p className="text-[10.5px] text-slate-400">
                    Identify your warehouse & main point of contact
                  </p>
                </div>
              </div>
              <span className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/60 px-2 py-0.5 rounded-full border border-indigo-200 dark:border-indigo-800">
                Step 1
              </span>
            </div>

            <div className="space-y-3.5">
              {/* Select Retailer (Admin Only) */}
              {role === 'admin' && (
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                    Assign to Retailer <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <Select
                      options={retailerIdData}
                      labelField="label"
                      valueField="value"
                      searchBy="label"
                      searchable={true}
                      placeholder="Search and select retailer..."
                      onChange={handleRetailerSelect}
                      color="#4f46e5"
                      style={{
                        paddingLeft: '0.85rem',
                        paddingRight: '0.85rem',
                        paddingTop: '0.5rem',
                        paddingBottom: '0.5rem',
                        borderRadius: '0.75rem',
                        fontSize: '0.75rem',
                        backgroundColor: isDarkMode ? '#0F172A' : '#f8fafc',
                        borderColor: retailerError ? '#f43f5e' : (isDarkMode ? '#334155' : '#e2e8f0'),
                        color: isDarkMode ? '#ffffff' : '#0f172a',
                      }}
                      dropdownHandleRenderer={() => (
                        <ChevronDown className="w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                      )}
                    />
                  </div>
                  {retailerError && (
                    <p className="flex items-center gap-1 text-[10.5px] font-semibold text-rose-500 mt-1">
                      <AlertCircle size={11} /> {retailerError}
                    </p>
                  )}
                </div>
              )}

              {/* Facility Name & Contact Person (2 Columns) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                {/* Facility Name */}
                <div>
                  <label htmlFor="facility-name" className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                    Facility Name <span className="text-rose-500">*</span>
                  </label>
                  <div className={inputContainerClass(errors.facilityName)}>
                    <Building2 className="absolute left-3 w-4 h-4 text-slate-400 pointer-events-none" />
                    <input
                      id="facility-name"
                      type="text"
                      placeholder="e.g. Noida Central Hub"
                      {...register('facilityName')}
                      className={inputClass}
                    />
                  </div>
                  {errors.facilityName && (
                    <p className="flex items-center gap-1 text-[10.5px] font-semibold text-rose-500 mt-1">
                      <AlertCircle size={11} /> {errors.facilityName.message}
                    </p>
                  )}
                </div>

                {/* Contact Person */}
                <div>
                  <label htmlFor="contact-person" className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                    Contact Person <span className="text-rose-500">*</span>
                  </label>
                  <div className={inputContainerClass(errors.contactPerson)}>
                    <User className="absolute left-3 w-4 h-4 text-slate-400 pointer-events-none" />
                    <input
                      id="contact-person"
                      type="text"
                      placeholder="e.g. Rahul Sharma"
                      {...register('contactPerson')}
                      className={inputClass}
                    />
                  </div>
                  {errors.contactPerson && (
                    <p className="flex items-center gap-1 text-[10.5px] font-semibold text-rose-500 mt-1">
                      <AlertCircle size={11} /> {errors.contactPerson.message}
                    </p>
                  )}
                </div>
              </div>

              {/* Phone & Email (2 Columns) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                    Mobile Number <span className="text-rose-500">*</span>
                  </label>
                  <div className={inputContainerClass(errors.phone)}>
                    <Phone className="absolute left-3 w-4 h-4 text-slate-400 pointer-events-none" />
                    <input
                      id="phone"
                      type="tel"
                      maxLength={10}
                      placeholder="10-digit mobile number"
                      {...register('phone')}
                      onChange={handlePhoneChange}
                      className={inputClass}
                    />
                  </div>
                  {errors.phone && (
                    <p className="flex items-center gap-1 text-[10.5px] font-semibold text-rose-500 mt-1">
                      <AlertCircle size={11} /> {errors.phone.message}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                    Email Address <span className="text-slate-400 text-[10px] font-normal">(Optional)</span>
                  </label>
                  <div className={inputContainerClass(errors.email)}>
                    <Mail className="absolute left-3 w-4 h-4 text-slate-400 pointer-events-none" />
                    <input
                      id="email"
                      type="email"
                      placeholder="hub@company.com"
                      {...register('email')}
                      className={inputClass}
                    />
                  </div>
                  {errors.email && (
                    <p className="flex items-center gap-1 text-[10.5px] font-semibold text-rose-500 mt-1">
                      <AlertCircle size={11} /> {errors.email.message}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ================= SECTION 2: LOCATION & ADDRESS ================= */}
          <div className={`rounded-2xl border p-4 sm:p-4.5 transition-colors ${
            isDarkMode
              ? 'bg-slate-900/60 border-slate-800'
              : 'bg-white border-slate-200/90 shadow-2xs'
          }`}>
            <div className="flex items-center justify-between pb-3 mb-3.5 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                  <MapPin size={15} />
                </div>
                <div>
                  <h3 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">
                    Address & Location
                  </h3>
                  <p className="text-[10.5px] text-slate-400">
                    City & State are auto-detected instantly from 6-digit Pincode
                  </p>
                </div>
              </div>
              <span className="text-[10px] font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/60 px-2 py-0.5 rounded-full border border-blue-200 dark:border-blue-800">
                Step 2
              </span>
            </div>

            <div className="space-y-3.5">
              {/* Pincode, City, State Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {/* Pincode */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label htmlFor="pincode" className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                      Pincode <span className="text-rose-500">*</span>
                    </label>
                    {pincodeVerified && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600 dark:text-emerald-400">
                        <CheckCircle2 size={11} /> Verified
                      </span>
                    )}
                  </div>
                  <div className={inputContainerClass(errors.pincode)}>
                    <Hash className="absolute left-3 w-4 h-4 text-slate-400 pointer-events-none" />
                    <input
                      id="pincode"
                      type="text"
                      maxLength={6}
                      placeholder="e.g. 201301"
                      {...register('pincode')}
                      onChange={handlePincodeChange}
                      className={inputClass}
                    />
                    {isFetchingPincode && (
                      <Loader2 className="absolute right-3 w-4 h-4 text-indigo-500 animate-spin" />
                    )}
                  </div>
                  {errors.pincode && (
                    <p className="flex items-center gap-1 text-[10.5px] font-semibold text-rose-500 mt-1">
                      <AlertCircle size={11} /> {errors.pincode.message}
                    </p>
                  )}
                </div>

                {/* City (Auto-filled) */}
                <div>
                  <label htmlFor="city" className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                    City <span className="text-slate-400 font-normal text-[10px]">(Auto)</span>
                  </label>
                  <div className={inputContainerClass(errors.city)}>
                    <MapPin className="absolute left-3 w-4 h-4 text-slate-400 pointer-events-none" />
                    <input
                      id="city"
                      type="text"
                      placeholder="Auto-detected"
                      {...register('city')}
                      disabled
                      className={inputClass}
                    />
                  </div>
                  {errors.city && (
                    <p className="flex items-center gap-1 text-[10.5px] font-semibold text-rose-500 mt-1">
                      <AlertCircle size={11} /> {errors.city.message}
                    </p>
                  )}
                </div>

                {/* State (Auto-filled) */}
                <div>
                  <label htmlFor="state" className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                    State <span className="text-slate-400 font-normal text-[10px]">(Auto)</span>
                  </label>
                  <div className={inputContainerClass(errors.state)}>
                    <Globe className="absolute left-3 w-4 h-4 text-slate-400 pointer-events-none" />
                    <input
                      id="state"
                      type="text"
                      placeholder="Auto-detected"
                      {...register('state')}
                      disabled
                      className={inputClass}
                    />
                  </div>
                  {errors.state && (
                    <p className="flex items-center gap-1 text-[10.5px] font-semibold text-rose-500 mt-1">
                      <AlertCircle size={11} /> {errors.state.message}
                    </p>
                  )}
                </div>
              </div>

              {/* Complete Address */}
              <div>
                <label htmlFor="address" className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                  Complete Address <span className="text-rose-500">*</span>
                </label>
                <div className={inputContainerClass(errors.address)}>
                  <MapPin className="absolute left-3 top-3 w-4 h-4 text-slate-400 pointer-events-none" />
                  <textarea
                    id="address"
                    rows={2}
                    placeholder="Plot/Shop No., Street, Landmark, Area name"
                    {...register('address')}
                    className="w-full py-2.5 pl-10 pr-3 text-xs bg-transparent rounded-xl outline-none transition-colors resize-none text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500"
                  />
                </div>
                {errors.address && (
                  <p className="flex items-center gap-1 text-[10.5px] font-semibold text-rose-500 mt-1">
                    <AlertCircle size={11} /> {errors.address.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* ================= SECTION 3: RETURN ADDRESS (RTO) ================= */}
          <div className={`rounded-2xl border p-4 sm:p-4.5 transition-colors ${
            isDarkMode
              ? 'bg-slate-900/60 border-slate-800'
              : 'bg-white border-slate-200/90 shadow-2xs'
          }`}>
            {/* Interactive Toggle Pill Switch */}
            <label className="flex items-start justify-between gap-3 cursor-pointer select-none">
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-lg bg-purple-50 dark:bg-purple-950/60 text-purple-600 dark:text-purple-400 flex items-center justify-center shrink-0 mt-0.5">
                  <RotateCcw size={15} />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-slate-900 dark:text-white">
                    Different Return Address (RTO)
                  </h4>
                  <p className="text-[10.5px] text-slate-500 dark:text-slate-400 mt-0.5">
                    Enable if undelivered packages should return to a separate location
                  </p>
                </div>
              </div>

              {/* Styled Switch */}
              <div className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
                <input
                  type="checkbox"
                  checked={showReturnAddress}
                  onChange={(e) => setShowReturnAddress(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-10 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-indigo-600 peer-checked:to-purple-600" />
              </div>
            </label>

            {/* Expandable Return Address Form Fields */}
            {showReturnAddress && (
              <div className="mt-4 pt-3.5 border-t border-slate-100 dark:border-slate-800 space-y-3.5 animate-fadeIn">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {/* Return Pincode */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label htmlFor="return-pincode" className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                        Return Pincode
                      </label>
                      {returnPincodeVerified && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600 dark:text-emerald-400">
                          <CheckCircle2 size={11} /> Verified
                        </span>
                      )}
                    </div>
                    <div className={inputContainerClass(errors.returnPincode)}>
                      <Hash className="absolute left-3 w-4 h-4 text-slate-400 pointer-events-none" />
                      <input
                        id="return-pincode"
                        type="text"
                        maxLength={6}
                        placeholder="e.g. 110001"
                        {...register('returnPincode')}
                        onChange={handleReturnPincodeChange}
                        className={inputClass}
                      />
                      {isFetchingReturnPincode && (
                        <Loader2 className="absolute right-3 w-4 h-4 text-purple-500 animate-spin" />
                      )}
                    </div>
                    {errors.returnPincode && (
                      <p className="flex items-center gap-1 text-[10.5px] font-semibold text-rose-500 mt-1">
                        <AlertCircle size={11} /> {errors.returnPincode.message}
                      </p>
                    )}
                  </div>

                  {/* Return City */}
                  <div>
                    <label htmlFor="return-city" className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                      Return City
                    </label>
                    <div className={inputContainerClass(errors.returnCity)}>
                      <MapPin className="absolute left-3 w-4 h-4 text-slate-400 pointer-events-none" />
                      <input
                        id="return-city"
                        type="text"
                        placeholder="Auto-detected"
                        {...register('returnCity')}
                        disabled
                        className={inputClass}
                      />
                    </div>
                  </div>

                  {/* Return State */}
                  <div>
                    <label htmlFor="return-state" className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                      Return State
                    </label>
                    <div className={inputContainerClass(errors.returnState)}>
                      <Globe className="absolute left-3 w-4 h-4 text-slate-400 pointer-events-none" />
                      <input
                        id="return-state"
                        type="text"
                        placeholder="Auto-detected"
                        {...register('returnState')}
                        disabled
                        className={inputClass}
                      />
                    </div>
                  </div>
                </div>

                {/* Return Address */}
                <div>
                  <label htmlFor="return-address" className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                    Return Address
                  </label>
                  <div className={inputContainerClass(errors.returnAddress)}>
                    <MapPin className="absolute left-3 top-3 w-4 h-4 text-slate-400 pointer-events-none" />
                    <textarea
                      id="return-address"
                      rows={2}
                      placeholder="Complete return destination address"
                      {...register('returnAddress')}
                      className="w-full py-2.5 pl-10 pr-3 text-xs bg-transparent rounded-xl outline-none transition-colors resize-none text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ================= STICKY FOOTER ACTIONS ================= */}
        <div className={`shrink-0 px-5 py-3.5 border-t flex items-center justify-end gap-3 backdrop-blur-md ${
          isDarkMode
            ? 'bg-slate-900/95 border-slate-800'
            : 'bg-white/95 border-slate-200'
        }`}>
          <button
            type="button"
            onClick={() => onClose && onClose()}
            disabled={isSubmitting}
            className="px-4 py-2 rounded-xl text-xs font-bold border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 transition-all active:scale-95 disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={isSubmitting}
            className="px-5 py-2 rounded-xl text-xs font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 hover:from-indigo-500 hover:to-purple-500 text-white shadow-md shadow-indigo-600/30 hover:shadow-indigo-600/50 flex items-center gap-2 transition-all active:scale-95 disabled:opacity-60"
          >
            {isSubmitting ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                <span>Creating Warehouse...</span>
              </>
            ) : (
              <>
                <Sparkles size={14} />
                <span>Create Warehouse</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddWarehouse;